import { logger } from '@teko/log';
import { trackManually } from '@teko/tracking';
import {
  CategoriesFilterOption,
  Filter,
  FilterOption,
} from '@teko/types/ecommerce';
import { useScreen, useTenantConfig } from 'contexts';
import { useSearchAlgo } from 'hooks/useSearchAlgo';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import { ParsedUrlQueryInput } from 'querystring';
import { useCallback, useEffect } from 'react';
import { ObjectMap } from 'types';
import {
  extractSortQuery,
  getDefaultSortOptions,
  omitQuery,
} from '../shared/helpers';
import { SortOptionInterface } from '../shared/types';

interface EventOptions {
  autoSubmit?: boolean;
  updatedQuery?: ParsedUrlQueryInput;
}

type OptionDictionary = Record<string, FilterOption>;

interface TrackingDataPayload {
  contentName: string;
  regionName: string;
  target: string;
  payload?: {
    attributeCode?: string | null;
    attributeName?: string | null;
    screenName?: string;
    url?: string;
  };
}

interface UseFilterOutput {
  clearFilterLink: string;
  getActiveFilterOptions: (filterOptions: Filter[]) => FilterOption[];
  onClickFilterOption: (option: FilterOption, options?: EventOptions) => void;
  onSortByPrice: (
    priceGte: number,
    priceLte: number,
    options?: EventOptions,
  ) => void;
  onSortOptionSelected: (
    option: SortOptionInterface,
    options?: EventOptions,
  ) => void;
  getActiveCategories: (
    categoriesFilters: Array<CategoriesFilterOption>,
  ) => Array<CategoriesFilterOption>;
  onClickCategoriesFilterOption: (
    option: CategoriesFilterOption,
    categoriesFilters: Array<CategoriesFilterOption>,
  ) => void;
  onRemoveCategory: (category: string) => void;
}

export const useFilter = (
  newQuery: ParsedUrlQueryInput,
  setNewQuery: (query: ParsedUrlQueryInput) => void,
  options?: EventOptions,
): UseFilterOutput => {
  const { defaultSortOptionCode, sortByCreatedAt } = useTenantConfig();
  const router = useRouter();
  const { query } = router;
  const { trackingScreenName } = useScreen();
  const { noActiveDefaultSortOption } = useSearchAlgo();
  const rootPath =
    typeof window !== 'undefined'
      ? window.location.pathname
      : router.asPath.split('?')[0];

  const clearFilterLink =
    rootPath === '/search'
      ? `/search?router=productListing${
          query.query ? `&query=${query.query}` : ''
        }`
      : `${
          typeof window !== 'undefined' ? window.location.origin : ''
        }${rootPath}`;

  useEffect(() => {
    setNewQuery(query);
  }, [JSON.stringify(query)]);

  const getActiveFilterOptions = useCallback(
    (filterOptions: Filter[]) => {
      const activatedFilters: Array<FilterOption> = [];
      const optionDictionary: OptionDictionary = filterOptions
        .reduce((acc, cur) => {
          return [...acc, ...cur.options];
        }, [] as FilterOption[])
        .reduce((acc, cur) => {
          const optionCode = cur.optionId;
          return { ...acc, [optionCode as string]: cur };
        }, {});
      Object.keys(newQuery).forEach((key) => {
        try {
          const values = newQuery[key];
          const filterValues =
            typeof values == 'string' ? values.split(',') : values;
          (filterValues as Array<string>).forEach((value) => {
            if (
              optionDictionary[value] &&
              key === optionDictionary[value].filterCode
            ) {
              activatedFilters.push(optionDictionary[value]);
            }
          });
        } catch (err) {
          logger.error(err);
          logger.error(`Problematic query key: ${key} value: ${newQuery[key]}`);
        }
      });
      return activatedFilters.map((filter) => ({
        ...filter,
      }));
    },
    [newQuery],
  );

  const changeRouteQuery = (routeQuery: ParsedUrlQueryInput) => {
    router.push(
      {
        pathname: router.pathname,
        query: routeQuery,
      },
      {
        pathname: rootPath,
        query: omitQuery(routeQuery),
      },
      { shallow: true },
    );
  };

  const modifyBehaviorBaseOnOptions = (
    routeQuery: ObjectMap,
    overrideOptions?: EventOptions,
  ) => {
    const { autoSubmit } = { ...options, ...overrideOptions };
    const { page, ...restQuery } = routeQuery;

    setNewQuery(restQuery);
    if (autoSubmit) {
      changeRouteQuery(routeQuery);
      const { price_gte, price_lte } = routeQuery;
      const targetUrl = new URL(window.location.origin);
      targetUrl.pathname = rootPath;
      targetUrl.search = new URLSearchParams(routeQuery).toString();
      trackManually(
        'applyFilter',
        'sortFilterProduct',
        trackingScreenName as string,
        JSON.stringify({
          screenName: trackingScreenName,
          ...(price_gte ? { validatedPriceGte: price_gte } : {}),
          ...(price_lte ? { validatedPriceLte: price_lte } : {}),
          url: targetUrl.toString(),
        }),
      );
    }
  };

  const onClickFilterOption = useCallback(
    (value: FilterOption, options?: EventOptions) => {
      const { page, ...filterParams } = newQuery;
      let routeQuery: ParsedUrlQueryInput = {};
      const targetUrl = new URL(window.location.origin);
      targetUrl.pathname = rootPath;
      const trackingData: TrackingDataPayload = {
        contentName: '',
        regionName: 'sortFilterProduct',
        target: trackingScreenName || '',
        payload: {},
      };
      if (value.filterCode === 'price') {
        const {
          ['price_gte']: tbr,
          ['price_lte']: tbr2,
          ...restFilterParams
        } = filterParams;
        routeQuery = restFilterParams;
      } else if (
        value.filterCode != undefined &&
        filterParams[value.filterCode]
      ) {
        // if filterCode existed
        const filterCodeValues =
          filterParams[value.filterCode]?.toString().split(',') || [];
        const index =
          value.optionId !== null
            ? filterCodeValues.indexOf(value.optionId.toString())
            : -1;
        // if this option is selected before
        if (index !== -1) {
          filterCodeValues.splice(index, 1);
        } else {
          // else if we are select a new option
          value.optionId !== null &&
            filterCodeValues.push(value.optionId.toString());
          trackingData.contentName = value.optionName;
          trackingData.payload = {
            attributeCode:
              value.optionId !== null ? value.optionId.toString() : null,
            attributeName: value.optionName,
            screenName: trackingScreenName,
          };
        }
        if (filterCodeValues.length) {
          routeQuery = {
            ...filterParams,
            [value.filterCode]: filterCodeValues.join(','),
          };
        } else {
          const { [value.filterCode]: tbr, ...restFilterParams } = filterParams;
          routeQuery = restFilterParams;
        }
      } else {
        routeQuery = {
          ...filterParams,
          ...(value.filterCode
            ? { [value.filterCode]: value.optionId?.toString() }
            : {}),
        };
        trackingData.contentName = value.optionName;
        trackingData.payload = {
          attributeCode: value.filterCode,
          attributeName: value.filterName,
          screenName: trackingScreenName,
        };
      }
      // if user select a filter then track
      if (trackingData.contentName) {
        targetUrl.search = new URLSearchParams(
          routeQuery as ObjectMap,
        ).toString();
        trackingData.payload = { url: targetUrl.toString() };
        trackManually(
          trackingData.contentName,
          trackingData.regionName,
          trackingData.target,
          JSON.stringify(trackingData.payload),
        );
      }
      modifyBehaviorBaseOnOptions(routeQuery, options);
    },
    [newQuery],
  );

  const onSortByPrice = useCallback(
    (priceGte = 0, priceLte = 0, options?: EventOptions): void => {
      const finalQuery = options?.updatedQuery || newQuery;
      const { page, price_gte, price_lte, ...filterParams } = finalQuery;
      const [validPriceGte, validPriceLte] =
        priceGte < priceLte ? [priceGte, priceLte] : [priceLte, priceGte];
      const routeQuery = {
        ...filterParams,
        ...(validPriceGte && { price_gte: validPriceGte }),
        ...(validPriceLte && { price_lte: validPriceLte }),
      };
      modifyBehaviorBaseOnOptions(routeQuery, options);
      trackManually(
        'sortProductResult',
        'sortFilterProduct',
        `${priceGte},${priceLte}`,
        'pricerange',
      );
    },
    [newQuery],
  );

  const onSortOptionSelected = useCallback(
    (option: SortOptionInterface, options?: EventOptions) => {
      const [sort, order] = extractSortQuery(option.optionCode);
      const defaultSortOption = defaultSortOptionCode
        ? getDefaultSortOptions(sortByCreatedAt).find(
            (option) => option.optionCode === t(defaultSortOptionCode),
          ) || null
        : null;
      const [defaultSort, defaultOrder] = defaultSortOption
        ? extractSortQuery(defaultSortOption.optionCode)
        : [undefined, undefined];
      const { page, ...filterParams } = newQuery;
      let routeQuery: ParsedUrlQueryInput;
      // case there is config default sort option name: do not allowed unselect selected option
      // case there is config default sort option name: do not show sort and order in query param
      if (
        ((!defaultSortOptionCode &&
          sort === query.sort &&
          order === query.order) ||
          (defaultSortOptionCode &&
            sort === defaultSort &&
            order === defaultOrder)) &&
        // case enable flag enableSearchAlgoABTesting === true, should go still allow
        // unselect selected option & show sort and order in query param
        !noActiveDefaultSortOption
      ) {
        const { sort: tbr, order: tbr2, ...restFilterParams } = filterParams;
        routeQuery = restFilterParams;
      } else {
        routeQuery = {
          ...filterParams,
          sort: sort,
          order: order,
        };
      }
      modifyBehaviorBaseOnOptions(routeQuery, options);
    },
    [newQuery],
  );

  const onClickCategoriesFilterOption = useCallback(
    (
      option: CategoriesFilterOption,
      categoriesFilters: Array<CategoriesFilterOption>,
    ) => {
      if (!option.code) return;
      const { categories, ...restQuery } = newQuery;
      const activeCatsArray: Array<string> = categories
        ? typeof categories == 'string'
          ? categories.split(',')
          : (categories as string[])
        : [];
      const activeCats = new Set(activeCatsArray);

      const addActiveChildren = (root: CategoriesFilterOption) => {
        if (!root.children.length) return;
        root.children.forEach((child) => {
          if (!!child.code && !!root.code && activeCats.has(root.code)) {
            activeCats.add(child.code);
          }
          addActiveChildren(child);
        });
      };
      categoriesFilters.forEach(addActiveChildren);

      const isCheck = !activeCats.has(option.code);
      const nodeById = new Map<number, CategoriesFilterOption>();
      const buildMapCatById = (cats: CategoriesFilterOption[]) => {
        cats.forEach((cat) => {
          if (typeof cat.id === 'number') {
            nodeById.set(cat.id, cat);
          }
          if (!!cat.children.length) {
            buildMapCatById(cat.children);
          }
        });
      };
      buildMapCatById(categoriesFilters);

      // user select new category
      if (isCheck) {
        // 1. add option
        activeCats.add(option.code);
        // 3. check all ancestors
        const checkParent = (root: CategoriesFilterOption) => {
          if (typeof root.parentId === 'number') {
            const parent = nodeById.get(root.parentId);
            const isAllChildrenChecked = parent?.children.every(
              (child) => !!child.code && activeCats.has(child.code),
            );
            if (!!parent?.code && isAllChildrenChecked) {
              activeCats.add(parent?.code);
              checkParent(parent);
            }
          }
        };
        checkParent(option);
      } else {
        // user unselect category
        // remove all children
        const deleteUnselectChildrendCode = (root: CategoriesFilterOption) => {
          if (!!root.code) {
            activeCats.delete(root.code);
            root.children.forEach((child) => {
              if (!!child.code) {
                child.children.forEach(deleteUnselectChildrendCode);
                activeCats.delete(child.code);
              }
            });
          }
        };
        deleteUnselectChildrendCode(option);
        // remove all ancestors
        const checkParent = (root: CategoriesFilterOption) => {
          if (typeof root.parentId === 'number') {
            const parent = nodeById.get(root.parentId);
            if (!!parent?.code && activeCats.has(parent?.code)) {
              activeCats.delete(parent.code);
              checkParent(parent);
            }
          }
        };
        checkParent(option);
        // remove option
        activeCats.delete(option.code);
      }

      // only keep parent selected code to push to url
      const urlCats = new Set<string>();
      const addRielCats = (root: CategoriesFilterOption) => {
        if (!root.code) return;
        if (activeCats.has(root.code)) {
          urlCats.add(root.code);
        }
        if (!!root.children.length && !activeCats.has(root.code)) {
          root.children.forEach(addRielCats);
        }
      };
      categoriesFilters.forEach(addRielCats);
      const modifiedQueries: ParsedUrlQueryInput = !!urlCats.size
        ? {
            ...restQuery,
            categories: [...urlCats].join(','),
          }
        : { ...restQuery };
      modifyBehaviorBaseOnOptions(modifiedQueries);
    },
    [newQuery],
  );

  const getActiveCategories = useCallback(
    (categoriesFilter: Array<CategoriesFilterOption>) => {
      const { categories } = newQuery;
      const activeOptionsArray: Array<string> = categories
        ? typeof categories == 'string'
          ? categories.split(',')
          : (categories as string[])
        : [];
      const activeCats = new Set(activeOptionsArray);

      const res: Array<CategoriesFilterOption> = [];
      const addActiveCats = (root: CategoriesFilterOption) => {
        if (!root.code) return;
        if (activeCats.has(root.code)) {
          res.push(root);
        } else {
          root.children.forEach(addActiveCats);
        }
      };
      categoriesFilter.forEach(addActiveCats);

      return res;
    },
    [newQuery],
  );

  const onRemoveCategory = useCallback(
    (category: string) => {
      const { categories, ...rest } = newQuery;
      const activeOptionsArray: Array<string> = categories
        ? typeof categories == 'string'
          ? categories.split(',')
          : (categories as string[])
        : [];
      const activeCats = new Set(activeOptionsArray);
      activeCats.delete(category);
      const modifiedQueries: ParsedUrlQueryInput = !!activeCats.size
        ? {
            ...rest,
            categories: [...activeCats].join(','),
          }
        : { ...rest };
      modifyBehaviorBaseOnOptions(modifiedQueries);
    },
    [newQuery],
  );

  return {
    clearFilterLink,
    getActiveFilterOptions,
    onClickFilterOption,
    onSortByPrice,
    onSortOptionSelected,
    getActiveCategories,
    onClickCategoriesFilterOption,
    onRemoveCategory,
  };
};
